import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth';
import '../App.css'; 
import { useRole } from './RoleContext';
import skillsData from '../data/SkillsData'; // Import the skills data


export let logOutb = false;

function Home() {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [lignip, setLignip] = useState(1); // 1 for Sign Up, 0 for Log In
  const [age, setAge] = useState('');
  const [showRoleChoice, setShowRoleChoice] = useState(false);
  const { setRole } = useRole();

  // State for messages
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  // State for Captcha
  const [diceValues, setDiceValues] = useState([]);
  const [captchaSum, setCaptchaSum] = useState(0);
  const [userCaptchaInput, setUserCaptchaInput] = useState('');

  // State to manage skill category open/close status
  // Initialize all categories as open by default
  const [openSkillCategories, setOpenSkillCategories] = useState(() => {
    const initialState = {};
    skillsData.forEach(category => {
      initialState[category.category] = false; // All open by default
    });
    return initialState;
  });

  // Function to toggle a skill category's open/close status
  const toggleSkillCategory = (categoryName) => {
    setOpenSkillCategories(prevState => ({
      ...prevState,
      [categoryName]: !prevState[categoryName]
    }));
  };

  // --- Message Handling ---
  const displayMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000); // Message disappears after 5 seconds
  };

const handleGoogleAuth = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check Firestore for existing user
    const userRef = doc(db, 'users', user.uid);
    const existing = await getDoc(userRef);

    if (!existing.exists()) {
      await setDoc(userRef, {
        fullName: user.displayName || '',
        email: user.email,
        emailVerified: user.emailVerified,
        createdAt: new Date(),
        role: 'mentee', // default role
        skills: [],
      });
    }

    displayMessage(`Welcome ${user.displayName || 'User'}!`, 'success');
    sessionStorage.setItem('user', user.email);
    setRole(existing.exists() ? existing.data().role : 'mentee');

  } catch (error) {
    console.error('Google login failed:', error);
    displayMessage('Google login failed. Try again.', 'error');
  }
};
  
  // --- Captcha Logic ---
  const generateDiceCaptcha = () => {
    const numberOfDice = 2; // 2 dice
    const newDiceValues = [];
    let currentSum = 0;
    for (let i = 0; i < numberOfDice; i++) {
      const value = Math.floor(Math.random() * 6) + 1; // 1 to 6 for each die
      newDiceValues.push(value);
      currentSum += value;
    }
    setDiceValues(newDiceValues);
    setCaptchaSum(currentSum);
    setUserCaptchaInput(''); // Clear user's previous input
  };

  const handleResetPassword = async () => {
  if (!email) {
    displayMessage('Please enter your email to reset password.', 'error');
    return;
  }

  try {
    await sendPasswordResetEmail(auth, email);
    displayMessage('Password reset email sent! Please check your inbox.', 'success');
  } catch (error) {
    displayMessage(error.message, 'error');
  }
};

  
  useEffect(() => {
    if (lignip === 1) { // Only generate captcha for signup
      generateDiceCaptcha();
    }
  }, [lignip]); // Regenerate when switching to signup

  // Update sessionStorage for 'user' when auth.currentUser changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      sessionStorage.setItem('user', user?.email || 'Previewer');
    });
    return () => unsubscribe();
  }, []);

  const handleSignUp = async () => {
    const numericAge = parseInt(age);

    if (!numericAge || numericAge < 0 || !email || !password || password.length < 8) {
      displayMessage("Please enter a valid age, email, and a password of at least 8 characters.", 'error');
      return;
    }

    if (numericAge < 11) {
      displayMessage("Sorry, you must be at least 11 years old to sign up.", 'error');
      return;
    }

    // Captcha validation
    if (parseInt(userCaptchaInput) !== captchaSum) {
      displayMessage("Incorrect captcha. Please try again.", 'error');
      generateDiceCaptcha(); // Regenerate captcha on failure
      return;
    }

    if (numericAge >= 5 && numericAge <= 25) {
      setRole('mentee');
      proceedToSignUp('mentee');
    } else if (numericAge >= 26 && numericAge <= 90) {
      setShowRoleChoice(true);
    } else {
      displayMessage("Please provide a valid age between 5 and 90.", 'error');
    }
    sessionStorage.setItem('age', numericAge);
  };

  const handleSkillChange = (e) => {
    const { value, checked } = e.target;

    if (checked) {
      if (selectedSkills.length < 4) {
        setSelectedSkills([...selectedSkills, value]);
      } else {
        displayMessage("You can select a maximum of 3 skills.", 'error');
      }
    } else {
      setSelectedSkills(selectedSkills.filter((skill) => skill !== value));
    }
  };

    const proceedToSignUp = async (finalRole) => {
    try {
      const ageStr = sessionStorage.getItem('age');
      const numericAge = Number(ageStr);
  
      if (!numericAge || numericAge < 11 || numericAge > 90) {
        displayMessage("Please provide a valid age (11–90).", 'error');
        return;
      }
  
      if (numericAge >= 26 && finalRole === 'mentor' && (!selectedSkills || selectedSkills.length === 0)) {
        displayMessage('As a mentor, you need at least 1 area of expertise.', 'error');
        return;
      }
  
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const uid = user.uid;
  
      await sendEmailVerification(user);
  
      await setDoc(doc(db, "users", uid), {
        email: email,
        role: finalRole,
        emailVerified: false, 
        ...(finalRole === 'mentor' && { skills: selectedSkills }),
      });
  
      setRole(finalRole);
      displayMessage(`Signed up as a ${finalRole}. A verification email has been sent to ${email}. Please verify your email before logging in.`, 'success');
  
      await signOut(auth);
      window.location.reload();
    } catch (error) {
      displayMessage(error.message + ' 🔒', 'error');
    }
  };


  const handleSignIn = async () => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (!user.emailVerified) {
      await signOut(auth);
      displayMessage('Your email address is not verified. Please check your inbox for a verification email.', 'error');
      return;
    }

    const uid = user.uid;
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, { emailVerified: true }, { merge: true });

    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      setRole(userData.role);
      displayMessage('You have been logged in. 🔓', 'success');
      logOutb = false;
      localStorage.setItem('logOutb', logOutb);
      window.location.reload();
    } else {
      displayMessage("No user data found in Firestore. Please contact support.", 'error');
      await signOut(auth);
    }
  } catch (error) {
    displayMessage(error.message + ' 🔒', 'error');
  }
};

  // const logOut = async () => {
  //   try {
  //     if (auth.currentUser) {
  //       await signOut(auth);
  //       logOutb = true;
  //       localStorage.setItem('logOutb', logOutb);
  //       window.location.reload();
  //     }
  //   } catch (error) {
  //     displayMessage(error.message + ' 🔒', 'error');
  //   }
  // };

  // Helper to render dice images based on their values
  const DiceDisplay = ({ values }) => (
    <div style={{ display: 'flex', gap: '5px', marginBottom: '10px', justifyContent: 'center' }}>
      {values.map((value, index) => (
        <span
          key={index}
          style={{
            display: 'inline-block',
            width: '35px',
            height: '35px',
            border: '2px solid #333',
            borderRadius: '8px',
            textAlign: 'center',
            lineHeight: '35px',
            fontSize: '20px',
            fontWeight: 'bold',
            backgroundColor: '#f0f0f0',
            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
            color: '#333'
          }}
        >
          {value}
        </span>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '20px auto', padding: '20px', border: '1px solid #ccc', borderRadius: '10px', boxShadow: '0 0 10px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '6px',
          textAlign: 'center',
          color: messageType === 'error' ? 'white' : '#333',
          backgroundColor: messageType === 'error' ? '#dc3545' : '#d4edda',
          fontWeight: 'bold',
          border: `1px solid ${messageType === 'error' ? '#c82333' : '#28a745'}`,
          fontSize: '1em'
        }}>
          {message}
        </div>
      )}

      <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '10px' }}>Welcome to Ventrai</h1>
      <p style={{ textAlign: 'center', color: '#555', marginBottom: '30px', fontSize: '1.1em' }}>Create an account or log in to continue.</p>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
        <button
          onClick={() => { setLignip(1); setShowRoleChoice(false); setAge(''); setSelectedSkills([]); }} // Reset states on tab switch
          style={{
            padding: '12px 20px',
            borderRadius: '8px',
            border: `2px solid ${lignip === 1 ? '#0056b3' : '#007bff'}`,
            backgroundColor: lignip === 1 ? '#007bff' : 'white',
            color: lignip === 1 ? 'white' : '#007bff',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '1.1em',
            transition: 'background-color 0.3s, color 0.3s, border-color 0.3s'
          }}
        >
          Create an account
        </button>
        <button
          onClick={() => { setLignip(0); setShowRoleChoice(false); setAge(''); setSelectedSkills([]); }} // Reset states on tab switch
          style={{
            padding: '12px 20px',
            borderRadius: '8px',
            border: `2px solid ${lignip === 0 ? '#1e7e34' : '#28a745'}`,
            backgroundColor: lignip === 0 ? '#28a745' : 'white',
            color: lignip === 0 ? 'white' : '#28a745',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '1.1em',
            transition: 'background-color 0.3s, color 0.3s, border-color 0.3s'
          }}
        >
          Log in
        </button>
      </div>

      {/* Common Inputs */}
{lignip !== 2 && (
  <>
    <div style={{ marginBottom: '15px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 'bold',
          color: '#333',
        }}
      >
        Email:
      </label>
      <input
        type="email"
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #ccc',
          boxSizing: 'border-box',
          fontSize: '1em',
        }}
      />
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 'bold',
          color: '#333',
        }}
      >
        Password:
      </label>
      <input
        type="password"
        placeholder="Password: *********"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #ccc',
          boxSizing: 'border-box',
          fontSize: '1em',
        }}
      />
    </div>

    {/* Google Auth Buttons */}
    <div style={{ textAlign: 'center', marginTop: '25px' }}>
      <button
        onClick={handleGoogleAuth}
        style={{
          width: '100%',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '12px',
          borderRadius: '6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '500',
          fontSize: '1em',
          transition: '0.2s',
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = '#f9f9f9')}
        onMouseLeave={(e) => (e.target.style.backgroundColor = 'white')}
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Google_Favicon_2025.svg"
          alt="Google logo"
          style={{ width: '22px', marginRight: '10px' }}
        />
        Continue with Google
      </button>
    </div>
  </>
)}


      {/* Sign Up Area */}
      {lignip === 1 && (
        <div style={{ borderTop: '1px solid #eee', paddingTop: '25px', marginTop: '25px', backgroundColor: '#fdfdfd', paddingBottom: '15px', borderRadius: '0 0 10px 10px' }}>
          <h2 style={{ color: '#555', textAlign: 'center', marginBottom: '25px', fontSize: '1.4em' }}>Sign Up Details</h2>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Your Age:</label>
            <input
              type="number"
              placeholder="Your age"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '1em' }}
            />
          </div>

          {/* Captcha Section */}
          <div style={{ marginBottom: '25px', padding: '18px', border: '1px solid #b0c4de', borderRadius: '10px', backgroundColor: '#eef5ff', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#444', fontSize: '1.1em' }}>Captcha <i class="bi bi-shield-check"></i>: What's the sum of these dice?</label>
            <DiceDisplay values={diceValues} />
            <input
              type="number"
              placeholder="Your answer"
              value={userCaptchaInput}
              onChange={(e) => setUserCaptchaInput(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #a0a0a0', boxSizing: 'border-box', fontSize: '1em' }}
            />
          </div>
          
          <button
            onClick={handleSignUp}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#007bff',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2em',
              fontWeight: 'bold',
              transition: 'background-color 0.3s ease'
            }}
          >
            Sign Up
          </button>

          {/* Role Choice and Skills Section (only shows if age is mentor-eligible) */}
          {showRoleChoice && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '25px', marginTop: '25px', backgroundColor: '#fdfdfd', paddingBottom: '15px' }}>
              <p style={{ textAlign: 'center', marginBottom: '20px', color: '#555', fontSize: '1.1em' }}>You are eligible to be a mentor or a mentee. Choose your role:</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
                <button onClick={() => { setRole('mentor'); proceedToSignUp('mentor'); }}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: '2px solid #4CAF50',
                    backgroundColor: '#5cb85c',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    transition: 'background-color 0.3s ease'
                  }}>
                  Mentor
                </button>
                <button onClick={() => proceedToSignUp('mentee')}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: '2px solid #FFA500',
                    backgroundColor: '#f0ad4e',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    transition: 'background-color 0.3s ease'
                  }}>
                  Mentee
                </button>
              </div>

              {/* Skills Grid */}
              <div style={{ width: '100%', border: '1px solid #dcdcdc', margin: '5px 0', padding: '15px', borderRadius: '8px', boxSizing: 'border-box', backgroundColor: '#fdfdfd' }}>
                <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', color: '#333', fontSize: '1.1em' }}>Please choose your best area of expertise. Max 5. Min 1</label>

                {skillsData.map(categoryData => (
                  <div key={categoryData.category} style={{ marginBottom: '20px' }}>
                    <h3
                      onClick={() => toggleSkillCategory(categoryData.category)}
                      style={{
                        borderBottom: '1px solid #eee',
                        paddingBottom: '8px',
                        marginBottom: '15px',
                        color: '#666',
                        fontSize: '1.2em',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      {categoryData.category}
                      <span style={{
                        fontSize: '1em',
                        transition: 'transform 0.3s ease',
                        transform: openSkillCategories[categoryData.category] ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}>
                        &#9650; {/* Unicode for a up arrow */}
                      </span>
                    </h3>
                    {openSkillCategories[categoryData.category] && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', // Adjust minmax for better 5-column layout
                        gap: '10px'
                      }}>
                        {categoryData.skills.map(skill => (
                          <div key={skill} style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              name="skills"
                              id={`skill-${skill.replace(/\s/g, '-')}`} // Unique ID for label association
                              value={skill}
                              onChange={handleSkillChange}
                              checked={selectedSkills.includes(skill)}
                              disabled={!selectedSkills.includes(skill) && selectedSkills.length >= 5}
                              style={{ marginRight: '8px' }}
                            />
                            <label htmlFor={`skill-${skill.replace(/\s/g, '-')}`} style={{ fontSize: '0.95em', color: '#444', flexGrow: 1 }}>{skill}</label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Log In Area */}
      {lignip === 0 && (
        <div style={{ borderTop: '1px solid #eee', paddingTop: '25px', marginTop: '25px', backgroundColor: '#fdfdfd', paddingBottom: '15px', borderRadius: '0 0 10px 10px' }}>
          <h2 style={{ color: '#555', textAlign: 'center', marginBottom: '25px', fontSize: '1.4em' }}>Log In</h2>
          {/* Reset Password Button */}
          <button
            onClick={handleResetPassword}
            style={{
              marginTop: '15px',
              color: '#1414ff',
              border: '0px',
              backgroundColor: 'white',
            }}
          >
            Reset Password
          </button>
          <p style={{ textAlign: 'center', color: '#777', marginBottom: '25px', fontSize: '1em' }}>Age and skills are not required for logging in.</p>
          <button
            onClick={handleSignIn}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#28a745',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2em',
              fontWeight: 'bold',
              transition: 'background-color 0.3s ease'
            }}
          >
            Log In
          </button>
        </div>
      )}

      {/* Log Out Button 
      {auth.currentUser && (
        <div style={{ borderTop: '1px solid #eee', paddingTop: '25px', marginTop: '25px', textAlign: 'center' }}>
          <button
            onClick={logOut}
            style={{
              padding: '12px 25px',
              borderRadius: '8px',
              border: '2px solid #c82333',
              backgroundColor: '#dc3545',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1.1em',
              transition: 'background-color 0.3s ease, border-color 0.3s ease'
            }}
          >
            Log Out
          </button>
        </div>
      )}*/}

      <p style={{ textAlign: 'center', color: 'black', marginTop: '30px', fontSize: '0.9em' }}><b>Please check your inbox or spam for email verification.</b></p>
    </div>
  );
}

export default Home;











